import { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { api } from '../../lib/api';
import { listVorgaenge, type Vorgang } from '../../lib/vorgangApi';
import { teamsApi, type TeamMember } from '../../lib/teamsApi';
import type { NeueAufgabe } from '../../lib/eigeneAufgabenApi';

interface KundeKurz {
  id: number;
  name: string;
}

/**
 * Feld, das beides kann: aus einer Liste wählen ODER frei tippen.
 *
 * Der getippte Text bleibt stehen, wenn nichts gewählt wird — ein Anruf betrifft manchmal
 * jemanden, der noch gar nicht angelegt ist. Erst ein Klick auf einen Vorschlag setzt die ID;
 * danach ist der Text gesperrt (das X löst ihn wieder), damit Name und ID nie auseinanderlaufen.
 */
function Kombifeld({
  label,
  placeholder,
  wert,
  vorschlaege,
  gewaehlt,
  onText,
  onWahl,
  disabled,
}: {
  label: string;
  placeholder: string;
  wert: string;
  vorschlaege: Array<{ id: number; text: string }>;
  gewaehlt: number | null;
  onText: (v: string) => void;
  onWahl: (id: number | null, text: string) => void;
  disabled?: boolean;
}) {
  const [offen, setOffen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const zu = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOffen(false);
    };
    document.addEventListener('mousedown', zu);
    return () => document.removeEventListener('mousedown', zu);
  }, []);

  const treffer = useMemo(() => {
    const q = wert.trim().toLowerCase();
    const basis = q ? vorschlaege.filter((v) => v.text.toLowerCase().includes(q)) : vorschlaege;
    return basis.slice(0, 8);
  }, [wert, vorschlaege]);

  return (
    <div ref={boxRef} className="relative">
      <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">{label}</label>
      <div className="relative">
        <input
          type="text"
          aria-label={label}
          value={wert}
          disabled={disabled || gewaehlt != null}
          placeholder={placeholder}
          onChange={(e) => { onText(e.target.value); setOffen(true); }}
          onFocus={() => setOffen(true)}
          className="w-full px-2.5 py-1.5 pr-7 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100 dark:disabled:bg-slate-800 placeholder:text-slate-400"
        />
        {(gewaehlt != null || wert) && (
          <button
            type="button"
            aria-label={`${label} leeren`}
            onClick={() => { onWahl(null, ''); setOffen(false); }}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {offen && gewaehlt == null && treffer.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full max-h-44 overflow-y-auto rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 shadow-lg">
          {treffer.map((v) => (
            <li key={v.id}>
              <button
                type="button"
                onClick={() => { onWahl(v.id, v.text); setOffen(false); }}
                className="w-full text-left px-2.5 py-1.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-blue-50 dark:hover:bg-slate-600"
              >
                {v.text}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function NeueAufgabeForm({
  onAnlegen,
  onAbbrechen,
}: {
  onAnlegen: (input: NeueAufgabe) => Promise<void>;
  onAbbrechen: () => void;
}) {
  const [kunden, setKunden] = useState<KundeKurz[]>([]);
  const [mitglieder, setMitglieder] = useState<TeamMember[]>([]);
  const [vorgaenge, setVorgaenge] = useState<Vorgang[]>([]);

  const [kundeText, setKundeText] = useState('');
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [vorgangText, setVorgangText] = useState('');
  const [vorgangId, setVorgangId] = useState<number | null>(null);
  const [titel, setTitel] = useState('');
  const [frist, setFrist] = useState('');
  const [zustaendig, setZustaendig] = useState<string>('');
  const [notiz, setNotiz] = useState('');
  const [punkte, setPunkte] = useState<string[]>([]);
  const [neuerPunkt, setNeuerPunkt] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  useEffect(() => {
    api.get<Array<{ id: number; name: string }>>('/api/customers')
      .then((l) => setKunden(l.map((c) => ({ id: c.id, name: c.name }))))
      .catch(() => setKunden([]));
    teamsApi.getMyTeam().then((t) => setMitglieder(t.members ?? [])).catch(() => setMitglieder([]));
  }, []);

  // Vorgänge erst laden, wenn ein Kunde gewählt ist — ohne Kunde gäbe es nichts zu wählen,
  // dann bleibt das Feld reines Freitextfeld.
  useEffect(() => {
    if (customerId == null) { setVorgaenge([]); return; }
    listVorgaenge(customerId).then(setVorgaenge).catch(() => setVorgaenge([]));
  }, [customerId]);

  const punktHinzu = () => {
    const t = neuerPunkt.trim();
    if (!t) return;
    setPunkte((p) => [...p, t]);
    setNeuerPunkt('');
  };

  const absenden = async () => {
    if (!titel.trim()) { setFehler('Bitte geben Sie an, was zu tun ist.'); return; }
    setLaeuft(true);
    setFehler(null);
    try {
      await onAnlegen({
        titel: titel.trim(),
        customerId,
        kundeFreitext: customerId == null ? kundeText.trim() || null : null,
        vorgangId,
        vorgangFreitext: vorgangId == null ? vorgangText.trim() || null : null,
        frist: frist || null,
        notiz: notiz.trim() || null,
        zustaendigUserId: zustaendig ? Number(zustaendig) : null,
        items: punkte.map((label) => ({ label })),
      });
    } catch (e: any) {
      setFehler(e?.message || 'Anlegen fehlgeschlagen.');
      setLaeuft(false);
    }
  };

  return (
    <div
      data-testid="neue-aufgabe-form"
      className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10 p-3 mb-3"
    >
      <div className="flex items-center justify-between mb-2.5">
        <span className="text-sm font-semibold text-slate-900 dark:text-white">Neue Aufgabe</span>
        <button
          type="button"
          onClick={onAbbrechen}
          aria-label="Formular schließen"
          className="p-1 rounded text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="space-y-2.5">
        <div>
          <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
            Was ist zu tun?
          </label>
          <input
            type="text"
            aria-label="Was ist zu tun?"
            value={titel}
            autoFocus
            onChange={(e) => setTitel(e.target.value)}
            placeholder="z.B. Rechnung nachfordern"
            className="w-full px-2.5 py-1.5 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-slate-400"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          <Kombifeld
            label="Kunde"
            placeholder="Suchen oder eintippen"
            wert={kundeText}
            gewaehlt={customerId}
            vorschlaege={kunden.map((k) => ({ id: k.id, text: k.name }))}
            onText={setKundeText}
            onWahl={(id, text) => {
              setCustomerId(id);
              setKundeText(text);
              // Kundenwechsel entwertet die Vorgangswahl — sonst hinge eine fremde ID dran.
              setVorgangId(null);
              setVorgangText('');
            }}
          />
          <Kombifeld
            label="Vorgang"
            placeholder={customerId == null ? 'Nummer eintippen' : 'Wählen oder eintippen'}
            wert={vorgangText}
            gewaehlt={vorgangId}
            vorschlaege={vorgaenge.map((v) => ({
              id: v.id,
              text: v.vorgangsnummer || v.bezeichnung || `Vorgang ${v.id}`,
            }))}
            onText={setVorgangText}
            onWahl={(id, text) => { setVorgangId(id); setVorgangText(text); }}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Frist</label>
            <input
              type="date"
              aria-label="Frist"
              value={frist}
              onChange={(e) => setFrist(e.target.value)}
              className="w-full px-2.5 py-1.5 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Zuständig</label>
            <select
              aria-label="Zuständig"
              value={zustaendig}
              onChange={(e) => setZustaendig(e.target.value)}
              className="w-full px-2.5 py-1.5 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Niemand</option>
              {mitglieder.map((m) => (
                <option key={m.id} value={m.id}>{m.contactPerson?.trim() || m.email}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Punkte</label>
          {punkte.length > 0 && (
            <ul className="mb-1.5 space-y-1">
              {punkte.map((p, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                  <input type="checkbox" disabled className="w-3.5 h-3.5 rounded" />
                  <span className="flex-1 truncate">{p}</span>
                  <button
                    type="button"
                    aria-label={`Punkt entfernen: ${p}`}
                    onClick={() => setPunkte((alt) => alt.filter((_, j) => j !== i))}
                    className="p-0.5 text-slate-400 hover:text-red-600"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex gap-1.5">
            <input
              type="text"
              aria-label="Punkt hinzufügen"
              value={neuerPunkt}
              onChange={(e) => setNeuerPunkt(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); punktHinzu(); } }}
              placeholder="Punkt hinzufügen"
              className="flex-1 px-2.5 py-1.5 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-slate-400"
            />
            <button
              type="button"
              onClick={punktHinzu}
              aria-label="Punkt übernehmen"
              className="px-2 rounded-md border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-700"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Notiz</label>
          <textarea
            rows={2}
            aria-label="Notiz"
            value={notiz}
            onChange={(e) => setNotiz(e.target.value)}
            placeholder="Optional"
            className="w-full px-2.5 py-1.5 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-slate-400 resize-none"
          />
        </div>

        {fehler && <p className="text-xs text-red-600 dark:text-red-400">{fehler}</p>}

        <div className="flex items-center justify-end gap-2 pt-0.5">
          <button
            type="button"
            onClick={onAbbrechen}
            className="px-2.5 py-1 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-700"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={absenden}
            disabled={laeuft}
            className="px-3 py-1 text-xs font-medium rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white"
          >
            {laeuft ? 'Legt an…' : 'Anlegen'}
          </button>
        </div>
      </div>
    </div>
  );
}
